import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React, {useState} from 'react'
import styled, {createGlobalStyle} from 'styled-components'

import SEO from '@/components/seo'
import StepWizard from '@/components/step-wizard'
import type {ExpensePolicyBuilderQuery} from '@/graphql-types'
import createPersistedReducer from '@/hooks/use-persisted-reducer'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {generatePDF, usePDFValues} from '@/utils/html-to-pdf'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import type {PrismicData} from '@helios/shared/types/prismic-data'
import mq from '@helios/shared/utils/media-query'

import Preview from './preview'
import Accommodation from './steps/accommodation'
import AllDone from './steps/all-done'
import Conferences from './steps/conferences'
import Details from './steps/details'
import Food from './steps/food'
import Gifts from './steps/gifts'
import Remote from './steps/remote'
import Transport from './steps/transport'
import Welcome from './steps/welcome'

const initialState = {
    welcome: {
        companyName: '',
    },
    accommodation: {
        howToBookTrips: '',
        budgetPerNight: '',
        coveredByPerDiem: false,
        extra: '',
    },
    food: {
        breakfast: '',
        lunch: '',
        dinner: '',
        breakfastCoveredByPerDiem: false,
        lunchCoveredByPerDiem: false,
        dinnerCoveredByPerDiem: false,
        extra: '',
    },
    transport: {
        airTravelPolicy: '',
        cityTravelPolicy: '',
        mileageAmount: '',
        extra: '',
    },
    conferences: {
        conferenceAmount: '',
        extra: '',
    },
    remote: {
        hardwareAmount: '',
        softwareAmount: '',
        extra: '',
    },
    gifts: {
        clientAmount: '',
        employeeAmount: '',
        extra: '',
    },
    details: {
        detailDays: '',
        detailsReimbursement: '',
        contactName: '',
        contactEmail: '',
        extra: '',
    },
    allDone: {
        email: '',
        consented: false,
    },
}

export type State = typeof initialState
export type SetValue<TGroup extends keyof State> = (key: keyof State[TGroup], value: any) => void
export type Data = PrismicData<ExpensePolicyBuilderQuery, 'allPrismicExpensePolicyBuilder'>['data']

enum ActionType {
    SET_VALUE,
    RESET_VALUES,
}

declare namespace Actions {
    export type SetValue = {
        type: ActionType.SET_VALUE
        group: keyof State
        key: any
        value: any
    }

    export type ResetValues = {
        type: ActionType.RESET_VALUES
        group: keyof State
    }
}

type Action = Actions.SetValue | Actions.ResetValues

const reducer = (state: State, action: Action) => {
    switch (action.type) {
        case ActionType.SET_VALUE: {
            return {
                ...state,
                [action.group]: {
                    ...state[action.group],
                    [action.key]: action.value,
                },
            }
        }

        case ActionType.RESET_VALUES: {
            return {
                ...state,
                [action.group]: initialState[action.group],
            }
        }

        default: {
            throw new Error('Unrecognized action')
        }
    }
}

const usePersistedReducer = createPersistedReducer<State>('ExpensePolicyBuilder')

type Props = PageProps<ExpensePolicyBuilderQuery>

const ExpensePolicyBuilder = ({data}: Props) => {
    const {something_went_wrong} = useGlobalStrings()
    const [state, dispatch] = usePersistedReducer(reducer, initialState)
    const [showPreview, setShowPreview] = useState(false)
    const {previewValues, isPreviewMode} = usePDFValues('expense-policy-builder', initialState)

    const nodeData = data.allPrismicExpensePolicyBuilder.nodes[0].data

    if (!data) {
        return handleNoPrismicData()
    }

    const togglePreview = () => {
        setShowPreview((showPreview) => !showPreview)
    }

    const setValue = <TGroup extends keyof State>(group: TGroup) => {
        return <TKey extends keyof State[TGroup]>(key: TKey, value: State[TGroup][TKey]) => {
            dispatch({
                type: ActionType.SET_VALUE,
                group,
                key,
                value,
            })
        }
    }

    const resetValues = <TGroup extends keyof State>(group: TGroup) => {
        return () => {
            dispatch({
                type: ActionType.RESET_VALUES,
                group,
            })
        }
    }

    const getPDF = async () => {
        try {
            const sharableLink = await generatePDF('expense-policy-builder', state)

            return sharableLink
        } catch (error) {
            throw new Error(something_went_wrong)
        }
    }

    const preview = (
        <Preview
            visible={showPreview}
            state={state}
            togglePreview={togglePreview}
            data={nodeData.preview[0]}
        />
    )

    if (isPreviewMode) {
        return (
            <Preview
                visible
                state={previewValues}
                togglePreview={togglePreview}
                data={nodeData.preview[0]}
                isPreviewMode
            />
        )
    }

    return (
        <>
            <PageStyles />

            <StepWizard>
                <Welcome
                    state={state.welcome}
                    setValue={setValue('welcome')}
                    data={nodeData.welcome[0]}
                >
                    {preview}
                </Welcome>

                <Accommodation
                    state={state.accommodation}
                    setValue={setValue('accommodation')}
                    resetValues={resetValues('accommodation')}
                    togglePreview={togglePreview}
                    data={{sidebar: nodeData.sidebar[0], ...nodeData.accommodation[0]}}
                >
                    {preview}
                </Accommodation>

                <Food
                    state={state.food}
                    setValue={setValue('food')}
                    resetValues={resetValues('food')}
                    togglePreview={togglePreview}
                    data={{sidebar: nodeData.sidebar[0], ...nodeData.food[0]}}
                >
                    {preview}
                </Food>

                <Transport
                    state={state.transport}
                    setValue={setValue('transport')}
                    resetValues={resetValues('transport')}
                    togglePreview={togglePreview}
                    data={{sidebar: nodeData.sidebar[0], ...nodeData.transport[0]}}
                >
                    {preview}
                </Transport>

                <Conferences
                    state={state.conferences}
                    setValue={setValue('conferences')}
                    resetValues={resetValues('conferences')}
                    togglePreview={togglePreview}
                    data={{sidebar: nodeData.sidebar[0], ...nodeData.conferences[0]}}
                >
                    {preview}
                </Conferences>

                <Remote
                    state={state.remote}
                    setValue={setValue('remote')}
                    resetValues={resetValues('remote')}
                    togglePreview={togglePreview}
                    data={{sidebar: nodeData.sidebar[0], ...nodeData.remote[0]}}
                >
                    {preview}
                </Remote>

                <Gifts
                    state={state.gifts}
                    setValue={setValue('gifts')}
                    resetValues={resetValues('gifts')}
                    togglePreview={togglePreview}
                    data={{sidebar: nodeData.sidebar[0], ...nodeData.gifts[0]}}
                >
                    {preview}
                </Gifts>

                <Details
                    state={state.details}
                    setValue={setValue('details')}
                    resetValues={resetValues('details')}
                    togglePreview={togglePreview}
                    data={{sidebar: nodeData.sidebar[0], ...nodeData.details[0]}}
                >
                    {preview}
                </Details>

                <AllDone
                    state={state.allDone}
                    setValue={setValue('allDone')}
                    togglePreview={togglePreview}
                    getPDF={getPDF}
                    data={{sidebar: nodeData.sidebar[0], ...nodeData.all_done[0]}}
                >
                    {preview}
                </AllDone>
            </StepWizard>
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<ExpensePolicyBuilderQuery>) => {
    const pageData = data.allPrismicExpensePolicyBuilder.nodes[0].data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

const PageStyles = createGlobalStyle`
    body {
        min-height: initial;
    }

    #___gatsby {
        overflow: initial;
    }
`

export const StepWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr;

    ${mq.min('md')} {
        grid-template-columns: 1fr 2fr;
    }
`

export const query = graphql`
    query ExpensePolicyBuilder($locale: String!) {
        allPrismicExpensePolicyBuilder(filter: {lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    meta_title
                    meta_description
                    open_graph_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    welcome {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        body
                        company_name
                        company_name_placeholder
                        start_button_label
                    }
                    sidebar {
                        preview
                        step
                        of
                        skip
                        next
                    }
                    accommodation {
                        title
                        subtitle
                        how_to_book_trips
                        how_to_book_trips_placeholder
                        budget_per_night
                        budget_per_night_placeholder
                        covered_by_per_diem
                        extra
                        extra_placeholder
                    }
                    food {
                        title
                        subtitle
                        breakfast
                        breakfast_placeholder
                        lunch
                        lunch_placeholder
                        dinner
                        dinner_placeholder
                        covered_by_per_diem
                        extra
                        extra_placeholder
                    }
                    transport {
                        title
                        subtitle
                        air_travel_policy
                        air_travel_policy_placeholder
                        city_travel_policy
                        city_travel_policy_placeholder
                        mileage_amount
                        mileage_amount_placeholder
                        extra
                        extra_placeholder
                    }
                    conferences {
                        title
                        subtitle
                        conference_amount
                        conference_amount_placeholder
                        extra
                        extra_placeholder
                    }
                    remote {
                        title
                        hardware_amount
                        hardware_amount_placeholder
                        software_amount
                        software_amount_placeholder
                        extra
                        extra_placeholder
                    }
                    gifts {
                        title
                        subtitle
                        client_amount
                        client_amount_placeholder
                        employee_amount
                        employee_amount_placeholder
                        extra
                        extra_placeholder
                    }
                    details {
                        title
                        subtitle
                        detail_days
                        detail_days_placeholder
                        details_reimbursement
                        details_reimbursement_placeholder
                        contact
                        contact_name
                        contact_email
                        extra
                        extra_placeholder
                    }
                    all_done {
                        title
                        subtitle
                        download_pdf
                        download_pdf_cta_text
                        sharable_link_cta_text
                        email
                        email_placeholder
                        privacy_policy {
                            richText
                        }
                        submit_text
                    }
                    preview {
                        accommodation_icon {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        food_icon {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        transport_icon {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        conferences_icon {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        remote_icon {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        gifts_icon {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        subtitle
                        accommodation_heading
                        how_to_book_trips
                        budget_per_night
                        budget_per_night_per_diem
                        food_heading
                        food_subtitle
                        food_covered_by_per_diem
                        breakfast
                        lunch
                        dinner
                        food_budget
                        transport_heading
                        air_travel_budget
                        city_transport_budget
                        mileage_budget
                        conferences_heading
                        conference_amount
                        remote_heading
                        hardware_amount
                        software_amount
                        gifts_heading
                        client_amount
                        employee_amount
                        details_heading
                        detail_days
                        detail_reimbursements
                        contact
                    }
                }
            }
        }
    }
`

export default ExpensePolicyBuilder
